import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { api, formatApiError, formatDateTime, INTERACTION_TYPES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MetadataFooter from "@/components/MetadataFooter";
import { Plus, Search, FileText, X, Pencil, FilePlus2, Save } from "lucide-react";

export default function WellnessNotes() {
  const { can } = useAuth();
  const [searchParams] = useSearchParams();
  const csId = searchParams.get("care_seeker_id");
  const [notes, setNotes] = useState([]);
  const [careSeekers, setCareSeekers] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(!!csId || searchParams.get("new") === "1");
  const [editingNote, setEditingNote] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (typeFilter) params.interaction_type = typeFilter;
      if (csId) params.care_seeker_id = csId;
      const [n, cs] = await Promise.all([
        api.get("/notes", { params }),
        careSeekers.length === 0 ? api.get("/care-seekers") : Promise.resolve({ data: careSeekers }),
      ]);
      setNotes(n.data);
      if (cs.data) setCareSeekers(cs.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, typeFilter, csId, careSeekers]);

  useEffect(() => { load(); }, [load]);

  const csName = (id) => {
    const cs = careSeekers.find((c) => c.id === id);
    if (!cs) return "Unknown";
    return `${cs.preferred_name || cs.first_name} ${cs.last_name}`;
  };

  // Per product owner: "Wellness notes should only be visible under Care
  // Seekers individual profile." If the URL doesn't scope to one care
  // seeker, bounce the user back to the list so they pick a profile first.
  if (!csId) {
    return <Navigate to="/care-seekers" replace />;
  }

  return (
    <div className="space-y-6" data-testid="wellness-notes-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Follow-up conversations & visits</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Follow-Up Notes</h1>
        </div>
        {can("notes.create") && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-note-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
          >
            <Plus className="w-4 h-4" /> Add note
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]">
          <Search className="w-4 h-4 text-[#1E3A8A]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search notes…"
            className="bg-transparent flex-1 outline-none text-sm"
            data-testid="notes-search"
          />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm" data-testid="notes-type-filter">
          <option value="">All types</option>
          {INTERACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button onClick={load} className="px-4 py-2 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] text-sm font-medium">Search</button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center">
          <FileText className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-1">No notes yet</h3>
          <p className="text-sm text-[#1E3A8A]">Capture referral coordination, community-resource navigation, partner follow-up, appointment logistics, service-status updates, and nonprofit program activities here.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="notes-list">
          {notes.map((n) => (
            <li key={n.id} className="bg-white border border-[#E5E5E0] rounded-2xl p-5 hw-card-hover">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-display font-semibold text-[#0F172A]">{csName(n.care_seeker_id)}</div>
                  <div className="text-xs text-[#1E3A8A] mt-0.5 capitalize">
                    {n.interaction_type?.replace(/_/g, " ")} · {formatDateTime(n.interaction_date)}
                  </div>
                </div>
                {can("notes.create") && (
                  <button
                    onClick={() => setEditingNote(n)}
                    data-testid={`edit-note-${n.id}`}
                    className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
              </div>
              {n.emotional_wellness_notes && <p className="text-sm text-[#334155] whitespace-pre-wrap mb-2">{n.emotional_wellness_notes}</p>}
              {n.next_steps && <div className="text-xs text-[#1E3A8A] mt-2"><strong className="text-[#334155]">Next:</strong> {n.next_steps}</div>}
              {n.follow_up_date && <div className="text-xs text-[#1E3A8A]"><strong className="text-[#334155]">Follow up:</strong> {n.follow_up_date}</div>}
              <MetadataFooter entity={n} className="mt-2 pt-2 border-t border-[#F0EFEB]" />
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <NoteForm
          careSeekers={careSeekers}
          defaultCsId={csId}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}
      {editingNote && (
        <NoteForm
          careSeekers={careSeekers}
          existing={editingNote}
          onClose={() => setEditingNote(null)}
          onCreated={() => { setEditingNote(null); load(); }}
        />
      )}
    </div>
  );
}

function NoteForm({ careSeekers, defaultCsId, onClose, onCreated, existing }) {
  const [form, setForm] = useState({
    care_seeker_id: existing?.care_seeker_id || defaultCsId || "",
    interaction_date: existing?.interaction_date
      ? new Date(existing.interaction_date).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
    interaction_type: existing?.interaction_type || "phone",
    emotional_wellness_notes: existing?.emotional_wellness_notes || "",
    resources_discussed: existing?.resources_discussed || "",
    referrals_made: existing?.referrals_made || "",
    next_steps: existing?.next_steps || "",
    follow_up_date: existing?.follow_up_date || "",
    notes: existing?.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const isEdit = !!existing;

  // Load templates on mount so the dropdown is available immediately
  useEffect(() => {
    if (isEdit) return; // editing existing — don't clutter with templates
    api.get("/note-templates").then(({ data }) => setTemplates(data || [])).catch(() => {});
  }, [isEdit]);

  const applyTemplate = (tplId) => {
    setSelectedTemplate(tplId);
    if (!tplId) return;
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    // Only overwrite fields the template actually defines, so the Care Seeker
    // and date/time the user already picked don't get clobbered.
    const next = { ...form };
    const fields = ["interaction_type", "emotional_wellness_notes", "resources_discussed", "referrals_made", "next_steps", "notes"];
    for (const f of fields) {
      if (tpl[f]) next[f] = tpl[f];
    }
    setForm(next);
    toast.success(`Applied "${tpl.name}" template`);
  };

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.care_seeker_id) {
      toast.error("Please select a Care Seeker");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        interaction_date: new Date(form.interaction_date).toISOString(),
      };
      if (isEdit) {
        // Don't send care_seeker_id on update (immutable)
        delete payload.care_seeker_id;
        await api.patch(`/notes/${existing.id}`, payload);
        toast.success("Note updated");
      } else {
        await api.post("/notes", payload);
        toast.success("Note saved");
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h2 className="font-display text-xl font-semibold">{isEdit ? "Edit" : "New"} Follow-Up Note</h2>
          <button onClick={onClose} className="p-1.5"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="note-form">
          {!isEdit && (
            <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="note-template-bar">
              <FilePlus2 className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.75} />
              <span className="text-xs text-[#1E3A8A] font-medium">Template:</span>
              <select
                value={selectedTemplate}
                onChange={(e) => applyTemplate(e.target.value)}
                className="flex-1 min-w-[160px] px-3 py-1.5 rounded-lg bg-white border border-[#E5E5E0] text-sm"
                data-testid="note-template-select"
              >
                <option value="">Choose a template…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowSaveTemplate(true)}
                data-testid="save-as-template-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-xs text-[#1E3A8A] font-medium hover:bg-white"
                title="Save the current note as a reusable template"
              >
                <Save className="w-3 h-3" /> Save as template
              </button>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Care Seeker</label>
            <select disabled={isEdit} value={form.care_seeker_id} onChange={(e) => update("care_seeker_id", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] disabled:bg-[#F9F8F6]" data-testid="note-cs-select">
              <option value="">Select…</option>
              {careSeekers.map((cs) => (
                <option key={cs.id} value={cs.id}>{cs.preferred_name || cs.first_name} {cs.last_name}</option>
              ))}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Date & time</label>
              <input type="datetime-local" value={form.interaction_date} onChange={(e) => update("interaction_date", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="note-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Type</label>
              <select value={form.interaction_type} onChange={(e) => update("interaction_type", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="note-type">
                {INTERACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Non-clinical emotional-support observations</label>
            <textarea value={form.emotional_wellness_notes} onChange={(e) => update("emotional_wellness_notes", e.target.value)} rows={3} className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]" data-testid="note-emotional" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Resources discussed</label>
            <textarea value={form.resources_discussed} onChange={(e) => update("resources_discussed", e.target.value)} rows={2} className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Next steps</label>
            <textarea value={form.next_steps} onChange={(e) => update("next_steps", e.target.value)} rows={2} className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Follow-up date</label>
            <input type="date" value={form.follow_up_date} onChange={(e) => update("follow_up_date", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="note-submit-btn" className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Save note"}
            </button>
          </div>
        </form>
        {showSaveTemplate && (
          <SaveTemplateModal
            form={form}
            onClose={() => setShowSaveTemplate(false)}
            onSaved={(tpl) => {
              setShowSaveTemplate(false);
              setTemplates((prev) => [...prev, tpl].sort((a, b) => a.name.localeCompare(b.name)));
              toast.success(`Saved template "${tpl.name}"`);
            }}
          />
        )}
      </div>
    </div>
  );
}

function SaveTemplateModal({ form, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Template name is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/note-templates", {
        name: name.trim(),
        description: description.trim() || null,
        interaction_type: form.interaction_type || null,
        emotional_wellness_notes: form.emotional_wellness_notes || null,
        resources_discussed: form.resources_discussed || null,
        referrals_made: form.referrals_made || null,
        next_steps: form.next_steps || null,
        notes: form.notes || null,
      });
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="save-template-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold">Save as template</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#1E3A8A] mb-4">
          We'll save the current note's body fields (not the Care Seeker or date) so teammates can re-use this layout.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1.5">Template name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Postpartum check-in"
              data-testid="template-name-input"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1.5">Description (optional)</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short hint for teammates"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-full border border-[#E5E5E0] text-[#334155] font-medium text-sm">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="template-save-submit"
              className="flex-1 py-2.5 rounded-full bg-[#1E3A8A] text-white font-medium text-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
